import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ErrorResponse, HttpClient } from '@connect-x-app/hybrid-data-access';
import { useAppEnvConfig } from '@connect-x-app/hybrid-state';
import { QueryKeys, ResponseErrorCodes } from '@connect-x-app/shared-domain-models';
import { EventType } from '@connect-x-app/shared-types';
import { useFirebaseConfig, useLogout } from '@connect-x-app/web-state';
import { toastWarning } from '@connect-x-app/web-ui';
import { useQueryClient } from '@tanstack/react-query';

const ME_ENDPOINT_SUFFIX = 'auth/me';

export const AxiosInterceptorProvider = ({ children }: PropsWithChildren) => {
  const [isInterceptorReady, setIsInterceptorReady] = useState(false);
  const { logEvent } = useFirebaseConfig();
  const { appConfig } = useAppEnvConfig();
  const logout = useLogout();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const handle403 = useCallback(
    (isAdminRoute: boolean, response: ErrorResponse) => {
      // If we get back a 403 from auth/me on admin endpoint, we navigate back to "regular" connect web.
      // Better handling of all this should be covered in AVIO-43058
      if (isAdminRoute && response.config?.url?.endsWith(ME_ENDPOINT_SUFFIX)) {
        toastWarning(t('ERRORS.PERMISSION_DENIED'));
        navigate('/');
      }
    },
    [navigate, t],
  );

  const handle401 = useCallback(
    (_: ErrorResponse) => {
      logEvent(EventType.LOGOUT, { reason: '401 on call' });
      logout({ redirectToLogin: true });
    },
    [logEvent, logout],
  );

  const handle404 = useCallback(
    (response: ErrorResponse) => {
      if (response.config?.url?.endsWith(ME_ENDPOINT_SUFFIX)) {
        logEvent(EventType.LOGOUT, { reason: '404 on auth/me' });
        logout();
      }
    },
    [logEvent, logout],
  );

  const onError = useCallback(
    (response: ErrorResponse) => {
      const isAdminRoute = location.pathname.startsWith('/admin');

      switch (response.status) {
        case 403: {
          handle403(isAdminRoute, response);
          break;
        }
        case 401: {
          handle401(response);
          break;
        }
        case 404:
          handle404(response);
          break;
        default:
          break;
      }

      switch (response.data.code) {
        case ResponseErrorCodes.INSUFFICIENT_PERMISSIONS:
          // The user's permissions changed server-side (e.g. their role was edited). Refetch the
          // authenticated user so the store updates and the route guards re-evaluate declaratively.
          // A hard reload would abort the in-flight auth/me and loop forever on the stale persisted
          // permissions. auth/me itself is excluded so the refresh can never re-trigger itself.
          if (isAdminRoute && !response.config?.url?.endsWith(ME_ENDPOINT_SUFFIX)) {
            queryClient.invalidateQueries({ queryKey: [QueryKeys.AUTHENTICATE] });
          }
          break;
        case ResponseErrorCodes.USER_INACTIVE:
        case ResponseErrorCodes.UNAUTHORIZED:
          logEvent(EventType.LOGOUT, { reason: 'User inactive or unauthorized' });
          logout();
          break;
        default:
          return;
      }
    },
    [handle401, handle403, handle404, logEvent, logout, queryClient],
  );

  useEffect(() => {
    if (!isInterceptorReady) {
      HttpClient.set401RequestInterceptor();
      HttpClient.setInstances(onError);
      setIsInterceptorReady(true);
    }
  }, [isInterceptorReady, onError]);

  useEffect(() => {
    HttpClient.setHeaders(appConfig);
  }, [appConfig]);

  if (!isInterceptorReady) {
    return null; // Avoid rendering children if the interceptor isn't ready
  }

  return <>{children}</>;
};
